import { useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { EyeIcon, EyeClosedIcon, HorizontalArrowsIcon } from '@/UI/Icons';
import { FlexCenVer } from '@/UI/styles';

import { Dropdown } from '@/UI/Dropdown/Dropdown';
import { I18N_KEYS } from '@/language/keys';
import { useIsHovered } from '@/hooks/useIsHovered';
import { useComparisonView, useNodeNullable, useNodeValue } from '../Recipe/FlowContext';
import { useCursor } from '../Recipe/FlowComponents/Editor/Designer/useCursor';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import { NodeViewer } from './Shared/FileViewer';
import type { ComparisonMode, NodeId, Vec2 } from 'web';
import type { ComparisonData } from '@/types/node';

export function ComparisonNode({ id, data }: { id: NodeId; data: ComparisonData }) {
  const locked = useNodeNullable(id)?.locked;
  const editable = !locked;
  const { t } = useTranslation();

  const view = useComparisonView(id);

  const reveal = useCallback(() => view.setMode({ type: 'toggle', reveal: true }), [view]);
  const conceal = useCallback(() => view.setMode({ type: 'toggle', reveal: false }), [view]);

  const revealed = data.kind?.type === 'toggle' && data.kind.reveal;

  const modeOptions = useMemo(
    () => [
      { id: 'toggle', label: t(I18N_KEYS.RECIPE_MAIN.NODES.COMPARISON.TOGGLE), value: 'toggle' } as const,
      { id: 'slider', label: t(I18N_KEYS.RECIPE_MAIN.NODES.COMPARISON.SLIDER), value: 'slider' } as const,
    ],
    [t],
  );

  const handleSetMode = useCallback(
    ({ id: type }: { id: ComparisonMode['type'] }) => {
      if (type === 'toggle') {
        view.setMode({ type, reveal: false });
      } else if (type === 'slider') {
        view.setMode({ type, position: { x: 0.5, y: 0.5 }, angle: Math.PI / 2 });
      }
    },
    [view],
  );

  const { isHovered, ...hoverProps } = useIsHovered();

  const hasBothInputs = data.inputA && data.inputB;
  const hasAnyInput = data.inputA || data.inputB;
  const cursor = useCursor(id);

  const isVideo = useNodeValue(id)?.type === 'video';
  const typeLabel = isVideo
    ? t(I18N_KEYS.RECIPE_MAIN.NODES.COMPARISON.VIDEO)
    : t(I18N_KEYS.RECIPE_MAIN.NODES.COMPARISON.IMAGE);
  const label1 = `${typeLabel} 1`;
  const label2 = `${typeLabel} 2`;

  const tagStyle = (position: Vec2, angle: number, first?: boolean) => ({
    pointerEvents: 'none',
    position: 'absolute',
    left: `${position.x * 100}%`,
    bottom: `${position.y * 100}%`,
    transform: `translate(-50%, 50%) rotate(${-angle}rad) translate(0, ${first ? '-' : ''}50px) rotate(${angle}rad)`,
  });

  return (
    <DynamicNode2 id={id} data={data} icon={<HorizontalArrowsIcon />}>
      <Box sx={{ position: 'relative', overflow: 'hidden' }} {...hoverProps}>
        <NodeViewer id={id} cursor={cursor} clickToPlayDisabled={isVideo} />

        {data.kind?.type === 'toggle' && hasAnyInput && (
          <FlexCenVer sx={{ position: 'absolute', top: 0, gap: 0.5, padding: 1 }}>
            <AppIconButton
              disabled={!(data.inputA && data.inputB)}
              onPointerDown={reveal}
              onPointerOut={conceal}
              onPointerUp={conceal}
              width={32}
              height={32}
              mode="on-light"
            >
              {revealed ? <EyeClosedIcon /> : <EyeIcon />}
            </AppIconButton>

            <Tag text={data.inputB && (!revealed || !data.inputA) ? label2 : label1} />
          </FlexCenVer>
        )}

        {data.kind?.type === 'slider' && isHovered && hasBothInputs && (
          <>
            <Box sx={tagStyle(data.kind.position, data.kind.angle, true)}>
              <Tag text={label1} />
            </Box>
            <Box sx={tagStyle(data.kind.position, data.kind.angle, false)}>
              <Tag text={label2} />
            </Box>
          </>
        )}
      </Box>

      <FlexCenVer
        sx={{
          width: '100%',
          mt: 2,
          gap: 0.5,
          justifyContent: 'start',
          pointerEvents: !editable ? 'none' : 'auto',
        }}
      >
        <Typography variant="body-sm-rg" sx={{ color: color.White64_T, lineHeight: 1 }}>
          {t(I18N_KEYS.RECIPE_MAIN.NODES.COMPARISON.MODE)}
        </Typography>
        <Dropdown
          size="small"
          value={data.kind?.type ?? 'toggle'}
          disabled={!editable}
          onChange={handleSetMode}
          options={modeOptions}
          width="96px"
        />
      </FlexCenVer>
    </DynamicNode2>
  );
}

function Tag({ text }: { text: string }) {
  return (
    <FlexCenVer sx={{ background: color.Black40_T, px: 0.5, pointerEvents: 'none', borderRadius: '2px' }}>
      <Typography variant="label-xs-rg">{text}</Typography>
    </FlexCenVer>
  );
}
